import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, ReactNode, useMemo } from 'react';
import cx from 'clsx';
import { NavLink } from 'react-router-dom';

import { HelperTooltip, UserAvatar } from '@/components';
import { useSwitch } from '@/hooks';
import { shortenAddress } from '@/utils';

import style from './index.module.css';

export const VipLogo = ({
  marginLeft,
  marginRight,
  // ToolTip
  ...rest
}: {
  marginLeft?: number;
  marginRight?: number;
}) => {
  return (
    <NavLink target="_blank" to="/paid" {...rest}>
      <div
        className={cx(style.container)}
        style={{
          marginLeft,
          marginRight,
        }}>
        <div className={style.name}>VIP</div>
      </div>
    </NavLink>
  );
};

export const VipLogoWithGift = ({
  marginLeft,
  marginRight,
  userInfo,
  toolTip,
  showActiveDays = true,
  ...rest
}: {
  marginLeft?: number;
  marginRight?: number;
  userInfo?: UserInfo;
  toolTip?: ReactNode;
  showActiveDays?: boolean;
}) => {
  const { t } = useTranslation();
  const { on: showVipOuterTip, turn: turnTipOuterTip } = useSwitch(true);

  const vipGiverDesc = userInfo?.vip_giver?.desc;

  const stopPropagation = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  }, []);

  const activeVipDaysTip = useMemo(
    () =>
      showActiveDays && userInfo?.active_vip_days
        ? t('VipLogo.activeDays', { count: userInfo?.active_vip_days })
        : null,
    [showActiveDays, userInfo?.active_vip_days, t],
  );

  const handleGiverVisibleChange = useCallback(
    (visible: boolean) => {
      turnTipOuterTip(!visible);
    },
    [turnTipOuterTip],
  );

  return (
    <HelperTooltip
      title={
        showVipOuterTip ? (
          <>
            {toolTip}
            {activeVipDaysTip ? (
              <div className={cx(style.tipItem, toolTip && style.hasList)}>{activeVipDaysTip}</div>
            ) : null}
          </>
        ) : null
      }
      disable={!toolTip && !activeVipDaysTip}>
      {vipGiverDesc ? (
        <NavLink target={'_blank'} to={`/paid`} {...rest} onClick={stopPropagation}>
          <div
            className={cx(style.containerWithGift)}
            style={{
              marginLeft,
              marginRight,
            }}>
            <div className={style.name}>VIP</div>
            <HelperTooltip
              onVisibleChange={handleGiverVisibleChange}
              title={
                <Trans
                  i18nKey="VipLogo.sendByOther"
                  values={{
                    name: vipGiverDesc?.user.web3_id
                      ? `@${vipGiverDesc?.user.web3_id}`
                      : shortenAddress(vipGiverDesc?.id),
                  }}>
                  Current VIP is sent by{' '}
                  <NavLink
                    className={style.userName}
                    target="_blank"
                    onClick={stopPropagation}
                    to={`/profile/${vipGiverDesc?.id}`}>
                    name
                  </NavLink>{' '}
                  as a gift
                </Trans>
              }>
              <div>
                <NavLink target="_blank" to={`/profile/${vipGiverDesc?.id}`}>
                  <UserAvatar
                    data={vipGiverDesc}
                    size={15}
                    enableGroupAvatar={false}
                    enableOfficialLogo={false}
                  />
                </NavLink>
              </div>
            </HelperTooltip>
          </div>
        </NavLink>
      ) : (
        <VipLogo marginLeft={marginLeft} marginRight={marginRight} {...rest} />
      )}
    </HelperTooltip>
  );
};
